import type { TitleBrief } from "@anisync/types";
import { InfoIcon, PlayIcon, StarIcon } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { fadeIn } from "../lib/motion";
import { cn } from "../lib/utils";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Skeleton } from "./ui/skeleton";

/**
 * Karuzela na stronie głównej.
 *
 * Trzy decyzje, każda z powodu, który widać dopiero na działającej stronie:
 *
 * **Auto-obrót zatrzymuje się na najechaniu i na fokusie.** Karuzela, która
 * przewija się, gdy ktoś właśnie czyta opis albo celuje w przycisk, jest
 * wroga — a przy nawigacji klawiaturą podmiana treści pod fokusem gubi miejsce.
 *
 * **`prefers-reduced-motion` wyłącza obrót całkowicie**, nie tylko skraca
 * przejście. Dla części osób ruch w tle jest przyczyną nudności, a nie kwestią
 * gustu.
 *
 * **Kropki są przyciskami**, nie znacznikami: bez nich nie da się wrócić do
 * planszy, która właśnie zniknęła, i nie da się obsłużyć karuzeli klawiaturą.
 *
 * **Plansza zajmuje całą szerokość okna i sięga pod nagłówek.** Kadr w ramce
 * z zaokrąglonymi narożnikami czyta się jak ilustracja w artykule; kadr dochodzący
 * do krawędzi czyta się jak ekran. Przy okazji obraz ma wtedy tyle miejsca, ile
 * ma baner z AniLista — a te są panoramiczne.
 *
 * **Przesunięcie palcem zmienia planszę.** Na dotyku kropki są celem zbyt małym,
 * żeby być główną drogą, a przesunięcie jest gestem, którego się w tym miejscu
 * oczekuje. Ruch pionowy ODDAJEMY stronie: gdyby karuzela go przechwytywała,
 * nie dałoby się przewinąć strony palcem położonym na kadrze.
 */

/** Ile poziomego ruchu uznajemy za przesunięcie, a nie za drgnięcie palca. */
const SWIPE_MIN_PX = 44;

/** Ile trzymać jedną planszę. Osiem sekund starcza na przeczytanie meta. */
const ROTATE_MS = 8000;

const FORMAT_LABELS: Record<string, string> = {
  tv: "TV",
  tv_short: "TV krótkie",
  movie: "Film",
  special: "Special",
  ova: "OVA",
  ona: "ONA",
  music: "Muzyczne",
};

export function HeroCarousel({ titles }: { titles: TitleBrief[] }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const swipeFrom = useRef<{ x: number; y: number } | null>(null);

  const step = useCallback(
    (delta: number) => {
      setIndex((current) => (current + delta + titles.length) % titles.length);
    },
    [titles.length],
  );

  useEffect(() => {
    if (titles.length <= 1 || paused) return;
    // Sprawdzamy w efekcie, nie raz przy montowaniu: ustawienie da się zmienić
    // w trakcie życia karty, a wtedy karuzela ma zareagować.
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const timer = setInterval(() => {
      setIndex((current) => (current + 1) % titles.length);
    }, ROTATE_MS);
    return () => clearInterval(timer);
  }, [titles.length, paused]);

  // Indeks trzymamy w granicach: lista przychodzi z serwera i może się skrócić
  // przy odświeżeniu, a wtedy `titles[index]` byłoby `undefined`.
  const current = titles[Math.min(index, titles.length - 1)];
  if (current === undefined) return null;

  const label = current.titleEnglish ?? current.titleRomaji;
  const meta = [
    current.seasonYear !== null ? String(current.seasonYear) : null,
    current.format !== null ? (FORMAT_LABELS[current.format] ?? current.format) : null,
    current.episodesTotal !== null ? `${current.episodesTotal} odc.` : null,
  ].filter((x): x is string => x !== null);

  return (
    /*
      Przesunięcie palcem i strzałki są DODATKOWĄ drogą do tego, co robią
      kropki-przyciski pod spodem — sekcja nie jest jedynym sposobem obsługi
      karuzeli, więc nie potrzebuje własnej roli ani miejsca w kolejności fokusu.
    */
    <section
      aria-label="Polecane tytuły"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
      onPointerDown={(e) => {
        // Tylko palec i rysik: przeciąganie myszą koliduje z zaznaczaniem tekstu,
        // a mysz ma kropki i strzałki.
        if (e.pointerType === "mouse") return;
        swipeFrom.current = { x: e.clientX, y: e.clientY };
      }}
      onPointerUp={(e) => {
        const from = swipeFrom.current;
        swipeFrom.current = null;
        if (from === null || titles.length <= 1) return;
        const dx = e.clientX - from.x;
        const dy = e.clientY - from.y;
        // Ruch bardziej pionowy niż poziomy to przewijanie strony, nie przesunięcie.
        if (Math.abs(dx) < SWIPE_MIN_PX || Math.abs(dx) <= Math.abs(dy)) return;
        step(dx < 0 ? 1 : -1);
      }}
      onKeyDown={(e) => {
        if (titles.length <= 1) return;
        if (e.key === "ArrowRight") {
          e.preventDefault();
          step(1);
        }
        if (e.key === "ArrowLeft") {
          e.preventDefault();
          step(-1);
        }
      }}
      // `-mt-5` znosi górny odstęp treści, żeby kadr sięgnął nagłówka i przeszedł
      // pod jego rozmyte tło. `touch-pan-y` oddaje stronie przewijanie w pionie.
      className="bleed-edge relative -mt-5 touch-pan-y overflow-hidden bg-card"
    >
      {current.bannerUrl !== null && (
        <img
          {...fadeIn}
          // `key` wymusza nowy element przy zmianie planszy, więc przeglądarka
          // nie pokazuje starego obrazu z nowym tekstem podczas wczytywania.
          key={current.id}
          src={current.bannerUrl}
          alt=""
          className="absolute inset-0 size-full object-cover"
        />
      )}

      {/*
        Podwójny scrim: pionowy pod tekst u dołu, poziomy pod kolumnę z treścią.
        Bez niego jasny baner zjada biały tekst i nie da się tego naprawić samym
        kolorem czcionki.
      */}
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(90deg, var(--color-background) 0%, color-mix(in oklab, var(--color-background) 75%, transparent) 45%, transparent 85%)," +
            "linear-gradient(0deg, var(--color-background) 0%, transparent 75%)",
        }}
      />

      {/* Wysokość w `svh`, nie w `rem`: kadr ma zajmować część OKNA, a nie
          stałą liczbę wierszy tekstu — inaczej w trybie TV zająłby cały ekran.
          Sufit w `rem`, bo na wysokim monitorze 58% okna to ekran samego banera,
          a pod nim dopiero zaczyna się to, po co się przyszło. */}
      <div className="page-gutter relative flex min-h-[24rem] flex-col justify-end gap-3 py-8 md:min-h-[min(58svh,40rem)] md:py-12">
        {/*
          Tekst planszy wchodzi razem z nowym obrazem. Klucz tylko na tym bloku,
          nie na całej kolumnie: przyciski i kropki zostają tymi samymi elementami,
          więc fokus z klawiatury nie gubi się przy zmianie planszy.
        */}
        <div key={current.id} className="enter-rise flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2">
            {current.status === "releasing" && (
              <Badge variant="success">
                <span className="size-1.5 rounded-full bg-success" />
                Emitowane
              </Badge>
            )}
            {current.averageScore !== null && (
              <Badge variant="secondary">
                <StarIcon className="text-warning" />
                {current.averageScore}
              </Badge>
            )}
          </div>

          <h2 className="max-w-2xl text-2xl leading-tight font-bold sm:text-3xl">{label}</h2>

          {meta.length > 0 && <p className="text-sm text-muted-foreground">{meta.join(" · ")}</p>}
        </div>

        <div className="mt-1 flex flex-wrap gap-2">
          <Button variant="hero" asChild>
            <Link to={`/tytul/${current.id}`}>
              <PlayIcon />
              Odtwarzaj
            </Link>
          </Button>
          <Button variant="outline" asChild>
            <Link to={`/tytul/${current.id}`}>
              <InfoIcon />
              Szczegóły
            </Link>
          </Button>
        </div>

        {titles.length > 1 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {titles.map((title, i) => (
              <button
                key={title.id}
                type="button"
                onClick={() => setIndex(i)}
                aria-label={`Pokaż ${title.titleEnglish ?? title.titleRomaji}`}
                aria-current={i === index}
                /*
                  `data-slot="inline"` wyłącza globalne powiększanie celów
                  dotknięcia: kropka rozdęta do 2,75 rem przestaje być kropką,
                  a rząd ośmiu takich zawinąłby się do drugiego wiersza. Pole
                  dotknięcia powiększamy przezroczystym odstępem — ujemny margines
                  zdejmuje go z układu, więc rząd zostaje ciasny. `bg-clip-content`
                  jest tu warunkiem: bez niego tło malowało też ten odstęp i kropka
                  rysowała się jako kafelek wysokości 1,4 rem zamiast kreski.
                */
                data-slot="inline"
                className={cn(
                  "focus-ring-own -my-2 box-content h-1.5 rounded-full bg-clip-content py-2 transition-all duration-200 outline-none",
                  "focus-visible:ring-[0.1875rem] focus-visible:ring-ring/50",
                  i === index
                    ? "w-8 bg-foreground"
                    : "w-4 bg-muted-foreground/40 hover:bg-muted-foreground",
                )}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

/**
 * Kształt karuzeli na czas wczytywania strony głównej.
 *
 * Wcześniej w tym czasie stało wprowadzenie dla pustej instancji („Oglądaj
 * razem…") i znikało po chwili — przez sekundę strona mówiła, że katalog jest
 * pusty, choć nie był.
 */
export function HeroCarouselSkeleton() {
  return (
    <section
      aria-label="Wczytuję polecane tytuły"
      aria-busy="true"
      className="bleed-edge relative -mt-5 overflow-hidden"
    >
      {/* Tło to zwykła karta, nie szkielet: linie tekstu niżej są szkieletami
          i na tle w tym samym kolorze zlałyby się z nim. */}
      <div aria-hidden="true" className="absolute inset-0 bg-card" />
      <div className="page-gutter relative flex min-h-[24rem] flex-col justify-end gap-3 py-8 md:min-h-[min(58svh,40rem)] md:py-12">
        <Skeleton className="h-5 w-24" />
        <Skeleton className="h-9 w-full max-w-md" />
        <Skeleton className="h-4 w-48" />
        <div className="mt-1 flex gap-2">
          <Skeleton className="h-9 w-32" />
          <Skeleton className="h-9 w-28" />
        </div>
      </div>
    </section>
  );
}
